import get from 'lodash/get';
import omit from 'lodash/omit';
import RestClient from '../RestClient/RestClient';
import { ITEMS_URL } from './CartApiConstants';
import {
    BASE_URL,
    DELIVERY_OPTIONS,
    DRAFT_ORDER,
    STORE_URL,
    STORES_URL,
} from '../Order/OrderApiConstants';
import { RestConfig, getHostConfig } from '../Common/RestConfig';
import { apiHosts } from '../RestClient/FetchHelper.config';

const isAbsoluteURL = (url) => /^[a-z][a-z0-9+.-]*:/.test(url);

const getUpsellUrl = (config) => {
    // donot modify url if url is absolute.
    if (isAbsoluteURL(config.url)) {
        return config;
    }

    const context = RestClient.getContext();
    const relativeIndex = config.url.indexOf('item-offerings');
    let url = config.url.substring(relativeIndex, config.url.length);

    // Change the version and accept-version header for desktop url
    url = `//${context.hostname}/v2/${url}`;
    config.acceptVersion = '4';
    config.url = url;
    return config;
};

export const addItemToCart = (params) => {
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${ITEMS_URL}`;
    config.accessToken = params.accessToken;
    config.xClientSource = params.payload.xClientSource;
    config.params = omit(params.payload, ['interstitial']);
    // Adding query param in URL since its a post request, later BOF teams can pass "source" as part of body
    const source = params.payload && params.payload.source;
    if (source) {
        config.url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${ITEMS_URL}?source=${params.payload.source}`;
    }
    if (get(params, 'payload.interstitial')) {
        config.interstitial = true;
    }
    return RestClient.post(config)
        .then((response) => response)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => {
                return error;
            }
        );
};

export const removeItem = (params) => {
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${ITEMS_URL}/${params.payload.id}`;
    config.accessToken = params.accessToken;
    return RestClient.delete(config)
        .then((response) => response)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => {
                console.log(`DELETE request failed : ${error}`);
                return error;
            }
        );
};

export const getStoreForCart = (params) => {
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.xAllAvailableItems = true;
    config.url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${ITEMS_URL}/${DELIVERY_OPTIONS}/${STORE_URL}`;
    config.accessToken = params.accessToken;
    config.xClientSource = params.payload.xClientSource;
    return RestClient.get(config)
        .then((res) => res)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => {
                console.log('FetchCartItem: fetchCartItem request failed: ', error);
            }
        );
};

export const updateCartItem = (updateParams) => {
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.params = updateParams.payload.params;
    config.xClientSource = updateParams.payload.xClientSource;
    config.url = `${BASE_URL}/${updateParams.accountId}/${DRAFT_ORDER}/${ITEMS_URL}/${updateParams.payload.id}`;
    if (updateParams.payload.interstitial) {
        config.interstitial = true;
    }
    if (updateParams.payload.params && updateParams.payload.params.shipToBopus) {
        config.xAllAvailableItems = true;
        delete updateParams.payload.params.shipToBopus;
    }
    config.accessToken = updateParams.accessToken;
    return RestClient.put(config)
        .then((res) => res)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => {
                console.log('updateCartItem: updateCartItem request failed: ', error);
            }
        );
};

export const updateStore = (params) => {
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.params = params.payload;
    config.url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${STORES_URL}/${params.payload.id}`;
    config.accessToken = params.accessToken;
    return RestClient.put(config)
        .then((res) => res)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => {
                console.log('updateCartItem: updateCartItem request failed: ', error);
            }
        );
};

export const updateCartStore = (params) => {
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.params = params.payload;
    config.xAllAvailableItems = true;
    config.xClientSource = params.payload.xClientSource;
    config.url =
        `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${ITEMS_URL}/${config.params.itemId}` +
        `/${DELIVERY_OPTIONS}/${config.params.bopusId}`;
    if (params.payload && params.payload.isUpdateCartStore) {
        config.url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${ITEMS_URL}/${DELIVERY_OPTIONS}/${STORE_URL}`;
    }
    delete config.params.itemId;
    delete config.params.bopusId;
    config.accessToken = params.accessToken;
    return RestClient.put(config)
        .then((res) => res)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => {
                console.log('updateCartItem: updateCartItem request failed: ', error);
            }
        );
};

export const getUpsellItems = (params) => {
    let config = {
        external: true,
        url: params.payload.url,
    };
    config = getUpsellUrl(config);

    return RestClient.get(config).then((json) => {
        console.log('upsell data', json);
        return json;
    });
};

export const getUpsellItemsBrowseAPI = (params) => {
    const config = {
        apiHost: apiHosts.browse,
        params: params.payload,
        credentials: 'include',
        url: 'upsell-aggregator',
        version: '/browse-aggregator/v1',
    };
    return RestClient.get(config).then((json) => json);
};

export const updateDefaultZipcode = (updateParams) => {
    //  reference for payload = [{ op: 'add', path: '/cart_zipcode', value: this.state.value }];
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.params = updateParams.payload;
    config.url = `${BASE_URL}/${updateParams.accountId}/${DRAFT_ORDER}/${ITEMS_URL}`;
    config.accessToken = updateParams.accessToken;
    return RestClient.patch(config)
        .then((res) => res)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => {
                console.log('updateDefaultZipcode: updateDefaultZipcode request failed: ', error);
            }
        );
};
